<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import BaixarRelatorio from './components/BaixarRelatorio.vue';
import AtalhoAnalitico from './components/AtalhoAnalitico.vue';
import FiltrosRelatorio from './components/FiltrosRelatorio.vue';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import subDays from 'date-fns/subDays';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';
import VisaoGeralAPI from 'dashboard/api/fandata/visaoGeral';
import AbasRelatorio from './components/AbasRelatorio.vue';
import LiveReports from './LiveReports.vue';

// T-149 · Visão Geral: os dez indicadores da operação no período.
//
// É a primeira tela que a Tamara abre de manhã, então ela responde "como foi a operação?" antes
// de qualquer detalhe. O que é DETALHE continua nas telas de sempre: onde dói é o Farol, quanto o
// Bira segurou é o Atendimento IA, quanto ficou com terceiro é a Pendência.
//
// A antiga "Visão geral" (ao vivo, com status de agente e mapas de calor) vira a segunda aba: ela
// responde "como está AGORA", que é outra pergunta e outro momento do dia.
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const ABA_INDICADORES = 'indicadores';
const ABA_AO_VIVO = 'aovivo';
const abaAtiva = ref(
  route.query.aba === ABA_AO_VIVO ? ABA_AO_VIVO : ABA_INDICADORES
);
const abas = computed(() => [
  { key: ABA_INDICADORES, label: t('VISAO_GERAL.ABAS.INDICADORES') },
  { key: ABA_AO_VIVO, label: t('VISAO_GERAL.ABAS.AO_VIVO') },
]);

const customDateRange = ref([subDays(new Date(), 29), new Date()]);
const selectedDateRange = ref(DATE_RANGE_TYPES.LAST_30_DAYS);
const sinceTs = ref(getUnixStartOfDay(customDateRange.value[0]));
const untilTs = ref(getUnixEndOfDay(customDateRange.value[1]));
// T-252: canal e lado. Nulo e 'todos' são o padrão: quem abre a tela vê a operação inteira.
const canalFiltrado = ref(null);
const ladoFiltrado = ref('todos');
const isLoading = ref(false);

const data = reactive({
  total: 0,
  indicadores: {},
  por_canal: [],
  bira_humano: {},
  recontato: {},
  reaberturas: {},
  serie_diaria: [],
});

// T-150j: volume clicável. O número sozinho fecha a pergunta pela metade: ela quer ver QUAIS
// conversas formam aquele número, sem refazer o filtro à mão na tela de conversas.
const abrirConversasDoCanal = linha => {
  router.push({
    name: 'inbox_dashboard',
    params: { accountId: route.params.accountId, inboxId: linha.inbox_id },
  });
};

// T-150k: gráfico de linhas por dia, humano × Bira. Desenhado em SVG puro: são duas séries e o
// projeto não precisa de mais uma biblioteca de gráfico para isso.
const GRAF = { largura: 720, altura: 180, pad: 24 };

// T-247: as seis métricas que a Tamara listou, cada uma com as duas linhas. Antes o gráfico
// mostrava só volume; agora ele é o mesmo desenho servindo seis leituras, com um seletor.
//
// `conversas` é a primeira porque é a que já estava no ar: quem abrir a tela vê o que via.
const METRICAS = [
  { chave: 'conversas', tempo: false },
  { chave: 'recebidas', tempo: false },
  { chave: 'enviadas', tempo: false },
  { chave: 'finalizadas', tempo: false },
  { chave: 'primeira_resposta', tempo: true },
  { chave: 'resolucao', tempo: true },
];
const metricaEscolhida = ref('conversas');
const metricaAtual = computed(
  () => METRICAS.find(m => m.chave === metricaEscolhida.value) || METRICAS[0]
);

const grafico = computed(() => {
  const serie = data.serie_diaria || [];
  if (serie.length < 2) return null;

  // Série antiga (sem `metricas`) continua desenhando volume: a tela não pode quebrar entre o
  // deploy do servidor e o do navegador de quem está com a aba aberta.
  const valor = (d, lado) =>
    d.metricas?.[metricaEscolhida.value]?.[lado] ?? (d[lado] || 0);

  const valores = serie.flatMap(d => [valor(d, 'bira'), valor(d, 'humano')]);
  const maximo = Math.max(1, ...valores.filter(v => v !== null));
  const passo = (GRAF.largura - GRAF.pad * 2) / (serie.length - 1);
  const y = v =>
    GRAF.altura - GRAF.pad - ((v || 0) / maximo) * (GRAF.altura - GRAF.pad * 2);

  // Dia sem medição (tempo nulo) INTERROMPE a linha em vez de virar zero. Zero desceria o
  // traço até o chão e diria "responderam na hora", que é o contrário de "não houve o que
  // responder".
  const caminho = lado => {
    let desenho = '';
    let caneta = false;
    serie.forEach((d, i) => {
      const v = valor(d, lado);
      if (v === null || v === undefined) {
        caneta = false;
        return;
      }
      desenho += `${caneta ? 'L' : 'M'} ${GRAF.pad + i * passo} ${y(v)} `;
      caneta = true;
    });
    return desenho.trim();
  };

  // T-247 (02/10): gráfico de ÁREA, e não de linha. Pedido da Tamara, "melhora a
  // visualização", e melhora mesmo: com duas séries sobrepostas a linha fina obriga a seguir o
  // traço com o olho, enquanto a área deixa a proporção entre Bira e humano legível de relance.
  //
  // A área é o MESMO caminho da linha, fechado até a base. Fecha por TRECHO, e não do primeiro
  // ao último ponto, porque a série pode ter buracos: dia sem medição interrompe o traço, e
  // fechar por cima do buraco pintaria área onde não houve dado.
  const area = lado => {
    const base = GRAF.altura - GRAF.pad;
    let desenho = '';
    let inicio = null;
    let ultimo = null;
    serie.forEach((d, i) => {
      const v = valor(d, lado);
      const x = GRAF.pad + i * passo;
      if (v === null || v === undefined) {
        if (inicio !== null) desenho += `L ${ultimo} ${base} L ${inicio} ${base} Z `;
        inicio = null;
        return;
      }
      desenho += `${inicio === null ? 'M' : 'L'} ${x} ${y(v)} `;
      if (inicio === null) inicio = x;
      ultimo = x;
    });
    if (inicio !== null) desenho += `L ${ultimo} ${base} L ${inicio} ${base} Z `;
    return desenho.trim();
  };

  return {
    maximo,
    bira: caminho('bira'),
    humano: caminho('humano'),
    areaBira: area('bira'),
    areaHumano: area('humano'),
    primeiroDia: serie[0].dia,
    ultimoDia: serie[serie.length - 1].dia,
  };
});

// O pico é lido em voz alta, então tempo vira "12min" e contagem fica número.
const picoDoGrafico = computed(() => {
  if (!grafico.value) return '';
  return metricaAtual.value.tempo
    ? duracao(grafico.value.maximo)
    : grafico.value.maximo;
});

// Tempo em segundos vira a unidade que se lê em voz alta. "4680 s" não é resposta para ninguém.
const duracao = segundos => {
  if (segundos === null || segundos === undefined) return '—';
  const s = Number(segundos);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  const horas = Math.floor(s / 3600);
  const min = Math.round((s % 3600) / 60);
  return min ? `${horas}h${String(min).padStart(2, '0')}` : `${horas}h`;
};

const pct = valor =>
  valor === null || valor === undefined ? '—' : `${valor}%`;

// Os dez, na ordem em que ela listou no documento de validação.
const cards = computed(() => {
  const i = data.indicadores;
  return [
    {
      key: 'VOLUME',
      valor: i.volume ?? 0,
      accent: 'text-n-slate-12',
      indicador: 'volume',
    },
    // T-251: finalizadas ao lado do volume, que é onde ela faz sentido: quantas entraram e
    // quantas saíram, lado a lado.
    {
      key: 'FINALIZADAS',
      valor: i.finalizadas ?? 0,
      accent: 'text-n-teal-11',
      dica: t('VISAO_GERAL.DICA_FINALIZADAS'),
    },
    {
      key: 'CONTACT_RATE',
      valor: i.contact_rate ?? '—',
      accent: 'text-n-blue-11',
    },
    {
      key: 'SLA',
      valor: pct(i.sla_no_prazo),
      accent: 'text-n-teal-11',
      indicador: 'sla',
    },
    { key: 'TMA', valor: duracao(i.tma_seg), accent: 'text-n-slate-12' },
    { key: 'FCR', valor: pct(i.fcr), accent: 'text-n-teal-11' },
    {
      key: 'ABANDONO',
      valor: pct(i.abandono),
      accent: 'text-n-amber-11',
      indicador: 'abandono',
      dica: t('VISAO_GERAL.DICA_ABANDONO'),
    },
    {
      key: 'CANCELAMENTO',
      valor: pct(i.cancelamento),
      accent: 'text-n-ruby-11',
      indicador: 'cancelamento',
      dica: t('VISAO_GERAL.DICA_CANCELAMENTO'),
    },
    {
      key: 'DEFLEXAO',
      valor: pct(i.deflexao),
      accent: 'text-n-blue-11',
      indicador: 'deflexao',
      dica: t('VISAO_GERAL.DICA_DEFLEXAO'),
    },
    {
      key: 'PRIMEIRA_RESPOSTA',
      valor: duracao(i.primeira_resposta_seg),
      accent: 'text-n-slate-12',
      dica: t('VISAO_GERAL.DICA_PRIMEIRA_RESPOSTA'),
    },
    {
      key: 'ESPERA',
      valor: duracao(i.espera_seg),
      accent: 'text-n-amber-11',
      dica: t('VISAO_GERAL.DICA_ESPERA'),
    },
    // T-249: o tempo que o Bira segurou antes de passar para gente. Fica ao lado da espera de
    // propósito: são as duas metades do que o torcedor aguardou, e antes vinham somadas num
    // número só, o que fazia a fila humana carregar atraso que não era dela.
    {
      key: 'TRANSBORDO',
      valor: duracao(i.transbordo_seg),
      accent: 'text-n-blue-11',
      dica: t('VISAO_GERAL.DICA_TRANSBORDO'),
    },
    // T-247 (02/10): a fila que ninguém respondeu era um bloco solto embaixo dos cartões, ao
    // lado do abandono. A Tamara pediu que virasse cartão como os outros, com a explicação no
    // tooltip: "virar um bignumber como os demais com a descrição no Tooltip e não um item
    // separado". O abandono já era cartão, então o bloco inteiro saiu.
    {
      key: 'SEM_ATENDIMENTO',
      valor: pct(i.sem_atendimento),
      accent: 'text-n-ruby-11',
      dica: t('VISAO_GERAL.DICA_SEM_ATENDIMENTO'),
    },
  ];
});

const lados = computed(() => {
  const b = data.bira_humano || {};
  return [
    { key: 'BIRA', dados: b.bira || {} },
    { key: 'HUMANO', dados: b.humano || {} },
  ];
});

// T-170: "clicar em pedido de cancelamento 2% e ver as conversas daquele indicador" (10/09).
//
// Média e razão (TMA, contact rate, primeira resposta, espera) não têm subconjunto e por isso
// não recebem o clique: oferecer uma lista que não explica o número seria pior que não oferecer.
//
// Para percentual de coisa BOA (SLA no prazo, deflexão, FCR), a lista é a do que deu ERRADO.
// Quem abre "SLA 95%" quer ver os 5%, e a dica embaixo do título diz isso com todas as letras.
const DICAS = { SLA: 'SLA', DEFLEXAO: 'DEFLEXAO' };
const detalhe = reactive({
  aberto: false,
  carregando: false,
  card: null,
  total: 0,
  conversas: [],
  ordem: 'antigas',
});

// As mesmas quatro ordens da Jornada do Torcedor. "Paradas há mais tempo" é a que mais serve
// aqui: nos indicadores de abandono e de fila sem resposta, o que dói é o que está parado.
const ORDENS = ['antigas', 'recentes', 'movimentacao', 'status'];
const PESO_STATUS = { open: 0, pending: 1, snoozed: 2, resolved: 3 };

const conversasOrdenadas = computed(() => {
  const lista = [...detalhe.conversas];
  if (detalhe.ordem === 'recentes') {
    return lista.sort((a, b) => b.criada_em - a.criada_em);
  }
  if (detalhe.ordem === 'movimentacao') {
    return lista.sort((a, b) => a.movimentado_em - b.movimentado_em);
  }
  if (detalhe.ordem === 'status') {
    return lista.sort(
      (a, b) =>
        (PESO_STATUS[a.status] ?? 9) - (PESO_STATUS[b.status] ?? 9) ||
        a.criada_em - b.criada_em
    );
  }
  return lista.sort((a, b) => a.criada_em - b.criada_em);
});

const dicaDoDetalhe = computed(() =>
  detalhe.card && DICAS[detalhe.card.key]
    ? t(`VISAO_GERAL.DETALHE_DICA_${DICAS[detalhe.card.key]}`)
    : null
);

const fecharDetalhe = () => {
  detalhe.aberto = false;
  detalhe.conversas = [];
};

// Os cartões que têm linha no gráfico de tendência. FCR e recontato ficam fora porque são
// cálculos de período e não reduzem a um dia (ver TendenciaIndicadores.vue).
const TENDENCIA_POR_CARTAO = {
  VOLUME: 'volume',
  SLA: 'sla_no_prazo',
  ABANDONO: 'abandono',
  SEM_ATENDIMENTO: 'sem_atendimento',
  DEFLEXAO: 'deflexao',
  CANCELAMENTO: 'cancelamento',
  CONTACT_RATE: 'contact_rate',
};

const rotaDaTendencia = computed(() => {
  const chave = TENDENCIA_POR_CARTAO[detalhe.card?.key];
  if (!chave) return null;
  return {
    name: 'conversation_reports',
    params: { accountId: route.params.accountId },
    query: { indicador: chave },
  };
});

const abrirDetalhe = async card => {
  if (!card.indicador) return;
  detalhe.aberto = true;
  detalhe.carregando = true;
  detalhe.card = card;
  detalhe.conversas = [];
  detalhe.ordem = 'antigas';
  try {
    const { data: res } = await VisaoGeralAPI.conversas({
      indicador: card.indicador,
      since: sinceTs.value,
      until: untilTs.value,
    });
    detalhe.total = res.total;
    detalhe.conversas = res.conversas || [];
  } catch (e) {
    useAlert(t('VISAO_GERAL.DETALHE_ERRO'));
    fecharDetalhe();
  } finally {
    detalhe.carregando = false;
  }
};

// Abre em OUTRA aba, e como link de verdade em vez de window.open: quem está lendo o relatório
// não quer perdê-lo para olhar uma conversa, e o <a> ainda dá de graça o clique do meio, o
// ctrl+clique e o "copiar endereço" que um <button> não dá.
const urlDaConversa = linha =>
  router.resolve({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: linha.display_id,
    },
  }).href;

// Mesmo vocabulário da Jornada do Torcedor: duas telas de relatório não podem chamar o mesmo
// estado por nomes diferentes.
const STATUS_LABEL = {
  open: 'Aberta',
  resolved: 'Resolvida',
  pending: 'Pendente',
  snoozed: 'Adiada',
};
const statusLabel = s => STATUS_LABEL[s] || s;

const quando = ts =>
  ts ? new Date(ts * 1000).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const fetchData = async () => {
  isLoading.value = true;
  try {
    const { data: res } = await VisaoGeralAPI.get({
      since: sinceTs.value,
      until: untilTs.value,
      inboxId: canalFiltrado.value,
      lado: ladoFiltrado.value === 'todos' ? undefined : ladoFiltrado.value,
    });
    Object.assign(data, res);
  } catch (e) {
    useAlert(t('VISAO_GERAL.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const onDateRangeChange = value => {
  const [start, end, rangeType] = value;
  customDateRange.value = [start, end];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  sinceTs.value = getUnixStartOfDay(start);
  untilTs.value = getUnixEndOfDay(end);
  fetchData();
};

onMounted(fetchData);
</script>

<template>
  <div class="flex flex-col w-full h-full px-6 py-4 overflow-auto">
    <div class="flex flex-col gap-3 mb-4">
      <h1 class="text-xl font-semibold text-n-slate-12">
        {{ t('VISAO_GERAL.HEADER') }}
      </h1>
      <p class="text-sm text-n-slate-11 -mt-1">{{ t('VISAO_GERAL.HINT') }}</p>
    </div>

    <AbasRelatorio v-model="abaAtiva" :abas="abas" />

    <LiveReports v-if="abaAtiva === 'aovivo'" />

    <template v-else>
      <div class="flex items-start mb-4 flex-wrap gap-3">
        <WootDatePicker
          v-model:date-range="customDateRange"
          v-model:range-type="selectedDateRange"
          @date-range-changed="onDateRangeChange"
        />
        <!-- T-260: baixa exatamente o que esta tela está mostrando. -->
        <!-- T-252: canal e lado. Trocar qualquer um recarrega a tela. -->
        <FiltrosRelatorio
          v-model:inbox-id="canalFiltrado"
          v-model:lado="ladoFiltrado"
          @update:inbox-id="fetchData"
          @update:lado="fetchData"
        />
        <BaixarRelatorio
          nome="visao-geral"
          :dados="data"
          :since="sinceTs"
          :until="untilTs"
        />
        <!-- T-260 (leitura A): o caminho para a quebra completa, que já existe numa tela só. -->
        <AtalhoAnalitico />
      </div>

      <!-- Os dez indicadores -->
      <div class="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div
          v-for="c in cards"
          :key="c.key"
          class="flex flex-col gap-1 p-4 rounded-xl border bg-n-solid-1 text-left"
          :class="
            c.indicador
              ? 'border-n-weak cursor-pointer hover:border-n-brand hover:bg-n-solid-2'
              : 'border-n-weak'
          "
          :role="c.indicador ? 'button' : null"
          :tabindex="c.indicador ? 0 : null"
          @click="abrirDetalhe(c)"
          @keydown.enter="abrirDetalhe(c)"
        >
          <span class="text-xs font-medium text-n-slate-11">
            {{ t(`VISAO_GERAL.KPI.${c.key}`) }}
          </span>
          <span class="text-2xl font-semibold" :class="c.accent">
            {{ c.valor }}
          </span>
          <!-- T-249/T-250: cartão cujo SIGNIFICADO mudou leva a explicação junto. Espera e
               transbordo eram um número só antes de 30/09, e quem conhecia a tela precisa ler na
               tela por que o de espera caiu. -->
          <span v-if="c.dica" class="text-xs text-n-slate-11">{{ c.dica }}</span>
        </div>
      </div>

      <!-- T-150k: volume por dia, humano × Bira -->
      <div
        v-if="grafico"
        class="rounded-xl border border-n-weak bg-n-solid-1 px-5 py-4 mb-6"
      >
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('VISAO_GERAL.SERIE_TITULO') }}
          </h3>
          <!-- T-247: o mesmo gráfico serve as seis métricas. Botões e não dropdown porque são
               seis e comparar entre elas é o uso: trocar tem que custar um clique. -->
          <div class="flex flex-wrap gap-1">
            <button
              v-for="m in METRICAS"
              :key="m.chave"
              type="button"
              class="px-2 py-0.5 text-xs transition-colors border rounded-md"
              :class="
                metricaEscolhida === m.chave
                  ? 'border-n-brand text-n-brand'
                  : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
              "
              @click="metricaEscolhida = m.chave"
            >
              {{ t(`VISAO_GERAL.SERIE_METRICA.${m.chave.toUpperCase()}`) }}
            </button>
          </div>
          <div class="flex items-center gap-4 text-xs">
            <span class="flex items-center gap-1.5 text-n-slate-11">
              <span class="w-3 h-0.5 bg-n-blue-9 inline-block" />
              {{ t('VISAO_GERAL.LADO.BIRA') }}
            </span>
            <span class="flex items-center gap-1.5 text-n-slate-11">
              <span class="w-3 h-0.5 bg-n-amber-9 inline-block" />
              {{ t('VISAO_GERAL.LADO.HUMANO') }}
            </span>
          </div>
        </div>
        <div class="overflow-x-auto">
          <svg :viewBox="`0 0 720 180`" class="w-full min-w-[480px] h-[180px]">
            <!-- As áreas vêm primeiro, e translúcidas, para a de cima não esconder a de baixo.
                 A linha continua desenhada por cima: é ela que dá a leitura precisa do valor. -->
            <path :d="grafico.areaBira" class="fill-n-blue-9/20" stroke="none" />
            <path :d="grafico.areaHumano" class="fill-n-amber-9/20" stroke="none" />
            <path
              :d="grafico.bira"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="text-n-blue-9"
            />
            <path
              :d="grafico.humano"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              class="text-n-amber-9"
            />
          </svg>
        </div>
        <div class="flex justify-between text-xs text-n-slate-11">
          <span>{{ grafico.primeiroDia }}</span>
          <span>{{ t('VISAO_GERAL.SERIE_PICO', { n: picoDoGrafico }) }}</span>
          <span>{{ grafico.ultimoDia }}</span>
        </div>
      </div>

      <!-- Bira x humano -->
      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden mb-6"
      >
        <div class="px-5 py-3 border-b border-n-weak">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('VISAO_GERAL.BIRA_HUMANO') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('VISAO_GERAL.BIRA_HUMANO_HINT') }}
          </p>
        </div>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-n-slate-11">
              <th class="text-left font-medium px-5 py-2">
                {{ t('VISAO_GERAL.COL_QUEM') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('VISAO_GERAL.COL_VOLUME') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('VISAO_GERAL.COL_PARTICIPACAO') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('VISAO_GERAL.COL_RESOLUCAO') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('VISAO_GERAL.COL_ESPERA') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="lado in lados"
              :key="lado.key"
              class="border-t border-n-weak"
            >
              <td class="px-5 py-2 font-medium text-n-slate-12">
                {{ t(`VISAO_GERAL.LADO.${lado.key}`) }}
              </td>
              <td class="px-3 py-2 text-center">
                {{ lado.dados.volume ?? 0 }}
              </td>
              <td class="px-3 py-2 text-center">
                {{ pct(lado.dados.participacao) }}
              </td>
              <td class="px-3 py-2 text-center">
                {{ pct(lado.dados.taxa_resolucao) }}
              </td>
              <td class="px-3 py-2 text-center">
                {{ duracao(lado.dados.espera_seg) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Recontato e reaberturas (T-157) -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('VISAO_GERAL.RECONTATO') }}
          </span>
          <p class="text-2xl font-semibold text-n-amber-11">
            {{ pct(data.recontato.taxa) }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{
              t('VISAO_GERAL.RECONTATO_HINT', {
                voltas: data.recontato.recontatos ?? 0,
                total: data.recontato.avaliadas ?? 0,
                // T-247 (02/10): a janela passa a APARECER. A Tamara pediu "considerar
                // reabertura de até 7 dias do fechamento", e sete dias já era a regra
                // (`RecontatoRelatorio::JANELA_PADRAO`) — o que faltava era a tela dizer qual
                // janela estava usando, em vez do genérico "voltaram na janela".
                dias: data.recontato.janela_dias ?? 7,
              })
            }}
          </p>
        </div>
        <div class="p-4 rounded-xl border border-n-weak bg-n-solid-1">
          <span class="text-xs font-medium text-n-slate-11">
            {{ t('VISAO_GERAL.REABERTURAS') }}
          </span>
          <p class="text-2xl font-semibold text-n-slate-12">
            {{ data.reaberturas.total ?? 0 }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{
              t('VISAO_GERAL.REABERTURAS_HINT', {
                cliente: data.reaberturas.cliente ?? 0,
                ativo: data.reaberturas.contato_ativo ?? 0,
              })
            }}
          </p>
        </div>
      </div>

      <!-- Por canal -->
      <div class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden">
        <div class="px-5 py-3 border-b border-n-weak">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('VISAO_GERAL.POR_CANAL') }}
          </h3>
        </div>
        <div v-if="!data.por_canal.length" class="px-5 py-10 text-center">
          <p class="text-sm text-n-slate-11">{{ t('VISAO_GERAL.EMPTY') }}</p>
        </div>
        <table v-else class="w-full text-sm">
          <thead>
            <tr class="text-n-slate-11">
              <th class="text-left font-medium px-5 py-2">
                {{ t('VISAO_GERAL.COL_CANAL') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('VISAO_GERAL.COL_VOLUME') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('VISAO_GERAL.KPI.SLA') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('VISAO_GERAL.KPI.DEFLEXAO') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('VISAO_GERAL.COL_ESPERA') }}
              </th>
              <th class="font-medium px-3 py-2 text-center">
                {{ t('VISAO_GERAL.KPI.CANCELAMENTO') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="linha in data.por_canal"
              :key="linha.inbox_id"
              class="border-t border-n-weak"
            >
              <td class="px-5 py-2 font-medium text-n-slate-12">
                {{ linha.canal }}
              </td>
              <td class="px-3 py-2 text-center">
                <button
                  type="button"
                  class="font-medium text-n-slate-12 hover:underline"
                  @click="abrirConversasDoCanal(linha)"
                >
                  {{ linha.volume }}
                </button>
              </td>
              <td class="px-3 py-2 text-center">
                {{ pct(linha.sla_no_prazo) }}
              </td>
              <td class="px-3 py-2 text-center">{{ pct(linha.deflexao) }}</td>
              <td class="px-3 py-2 text-center">
                {{ duracao(linha.espera_seg) }}
              </td>
              <td class="px-3 py-2 text-center">
                {{ pct(linha.cancelamento) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>

    <!-- T-170: mesmo padrão de drill da Jornada do Torcedor (painel lateral por cima), para as
         duas telas de relatório abrirem detalhe do mesmo jeito. -->
    <div v-if="detalhe.aberto" class="fixed inset-0 z-[80]">
      <div class="absolute inset-0 bg-black/30" @click="fecharDetalhe" />
      <div
        class="absolute right-0 top-0 bottom-0 w-full max-w-md bg-n-solid-1 border-l border-n-weak shadow-xl flex flex-col"
      >
        <div
          class="flex items-start justify-between px-5 py-3 border-b border-n-weak"
        >
          <div class="min-w-0">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t(`VISAO_GERAL.KPI.${detalhe.card.key}`) }}
            </h3>
            <p class="text-xs text-n-slate-11">
              {{ t('VISAO_GERAL.DETALHE_TOTAL', { total: detalhe.total }) }}
            </p>
            <!-- T-253: do número para a tendência. O clique no cartão já abre as conversas
                 por trás dele; este link responde a outra pergunta, "está melhorando ou
                 piorando", sem tomar o lugar da primeira. -->
            <router-link
              v-if="rotaDaTendencia"
              :to="rotaDaTendencia"
              class="inline-block mt-1 text-xs underline text-n-brand"
            >
              {{ t('VISAO_GERAL.VER_TENDENCIA') }}
            </router-link>
            <p v-if="dicaDoDetalhe" class="text-xs text-n-amber-11 mt-0.5">
              {{ dicaDoDetalhe }}
            </p>
          </div>
          <button
            class="p-1 text-n-slate-11 hover:text-n-slate-12"
            @click="fecharDetalhe"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
        <div class="flex-1 overflow-auto">
          <div
            v-if="!detalhe.carregando && detalhe.conversas.length"
            class="flex items-center gap-2 px-5 py-2 border-b border-n-weak flex-wrap"
          >
            <span class="text-xs text-n-slate-11">
              {{ t('VISAO_GERAL.DETALHE_ORDEM') }}
            </span>
            <button
              v-for="o in ORDENS"
              :key="o"
              class="px-2 py-0.5 text-xs rounded-md"
              :class="
                detalhe.ordem === o
                  ? 'bg-n-slate-4 text-n-slate-12 font-medium'
                  : 'text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="detalhe.ordem = o"
            >
              {{ t(`VISAO_GERAL.DETALHE_ORDEM_${o.toUpperCase()}`) }}
            </button>
          </div>
          <div v-if="detalhe.carregando" class="p-6 text-sm text-n-slate-11">
            {{ t('VISAO_GERAL.DETALHE_CARREGANDO') }}
          </div>
          <div
            v-else-if="!detalhe.conversas.length"
            class="p-6 text-sm text-n-slate-11"
          >
            {{ t('VISAO_GERAL.DETALHE_VAZIO') }}
          </div>
          <a
            v-for="linha in conversasOrdenadas"
            :key="linha.display_id"
            :href="urlDaConversa(linha)"
            target="_blank"
            rel="noopener"
            class="w-full text-left px-5 py-3 border-b border-n-weak hover:bg-n-alpha-1 flex items-center justify-between gap-3"
          >
            <div class="min-w-0">
              <div class="text-sm font-medium text-n-slate-12 truncate">
                {{ linha.contato || '—' }}
              </div>
              <div class="text-xs text-n-slate-11 truncate">
                TKT-{{ linha.display_id }} · {{ linha.assunto || '—' }}
              </div>
              <div class="text-xs text-n-slate-10 truncate">
                {{ linha.canal || '—' }} · {{ quando(linha.criada_em) }}
              </div>
            </div>
            <span
              class="text-xs shrink-0"
              :class="
                linha.status === 'resolved' ? 'text-n-teal-11' : 'text-n-amber-11'
              "
            >
              {{ statusLabel(linha.status) }}
            </span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
